import React from 'react';
import {
  AutoStoriesRounded,
  CheckRounded,
  ForumRounded,
  PsychologyRounded,
  RouteRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FadeInUp, Pressable, StaggerGroup, StaggerItem } from '@/components/motion';
import HeroEmblem from '@/components/three/HeroEmblem';
import { colors, radius, shadows } from '@/theme/tokens';
// 論点の件数はビルド時に数えた実数（scripts/seo/generate-learn-pages.mjs が生成）。
// **手で数字を書かない。** 論点を足したときに表記だけ古くなるのを防ぐため。
import { LEARN_STATS } from '@/generated/learnStats';
import { PREMIUM_CONFIG } from '@/components/PremiumUpgradeModal';

const valueCards = [
  {
    icon: PsychologyRounded,
    title: '過去問×AI演習',
    description: '科目別の過去問を、解説付きで着実に。理解度に合わせたAI問題で、次に解くべき論点へ進めます。',
  },
  {
    icon: AutoStoriesRounded,
    title: '触って学ぶ体感型解説',
    description: 'スライダーを動かすと損益分岐点も動く。教科書を読むだけでは得にくい理解を、操作しながら深めます。',
  },
  {
    icon: RouteRounded,
    title: 'ロードマップと進捗の可視化',
    description: '忘却曲線をもとに復習時期を整理。試験日までのカウントダウンと進捗を、ひと目で確認できます。',
  },
] as const;

const plans = [
  {
    name: '無料',
    lead: 'まずは基本機能から',
    features: ['過去問演習・解説', '学習履歴', 'コミュニティ', '体感型解説の冒頭'],
  },
  {
    name: 'プレミアム',
    lead: '学習量をさらに広げたい方へ',
    // 回数は手で書かない（2026-10-04）。表示が100でサーバの上限は200だった＝購入前の画面が実装とずれていた。
    features: ['AI問題を無制限で作成', `AIチャットを1日${PREMIUM_CONFIG.PREMIUM_DAILY_AI_CHAT_LIMIT}回`,
      `二次試験のAI添削を1日${PREMIUM_CONFIG.PREMIUM_DAILY_GRADE_LIMIT}回`, '体感型解説の演習・応用'],
  },
] as const;

const sectionSx = {
  py: { xs: 7, md: 9 },
} as const;

const NewLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const startForFree = () => navigate('/chusho-app');
  const openLearn = () => navigate('/chusho-app/learn');

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        overflowX: 'hidden',
        bgcolor: colors.bg,
        color: colors.textPrimary,
      }}
    >
      <Box
        component="section"
        aria-labelledby="landing-title"
        sx={{
          position: 'relative',
          borderBottom: `1px solid ${colors.border}`,
          background: `radial-gradient(circle at 50% 12%, ${alpha(colors.gold, 0.12)}, transparent 38%)`,
        }}
      >
        <Container maxWidth="md" sx={{ py: { xs: 4, sm: 5, md: 6 }, textAlign: 'center' }}>
          <FadeInUp>
            <HeroEmblem height={220} />
            <Typography
              id="landing-title"
              component="h1"
              variant="h1"
              sx={{
                mt: { xs: 1, md: 0 },
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                lineHeight: 1.25,
              }}
            >
              中小企業診断士、合格までの一本道。
            </Typography>
            <Typography
              sx={{
                mt: 2.5,
                mx: 'auto',
                maxWidth: 720,
                color: colors.textSecondary,
                fontSize: { xs: '1rem', sm: '1.08rem' },
                lineHeight: 1.9,
              }}
            >
              過去問×AI演習、触って学ぶ体感型解説、忘却曲線にもとづく復習計画を、無料で始められます。
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="center"
              sx={{ mt: 4, mx: 'auto', maxWidth: 500 }}
            >
              <Pressable style={{ flex: 1 }}>
                <Button fullWidth size="large" variant="contained" onClick={startForFree} sx={{ minHeight: 52 }}>
                  無料で始める
                </Button>
              </Pressable>
              <Pressable style={{ flex: 1 }}>
                <Button fullWidth size="large" variant="outlined" onClick={openLearn} sx={{ minHeight: 52 }}>
                  触って学ぶを見る
                </Button>
              </Pressable>
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: colors.textSecondary }}>
              登録だけなら無料・月額¥500のプレミアムはいつでも解約可
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              <Box component="a" href="/learn/" sx={{ color: 'primary.main' }}>
                まずは読むだけ: 1次{LEARN_STATS.primarySubjects}科目・{LEARN_STATS.primaryTopics}論点の要点解説（登録不要で読めます）
              </Box>
            </Typography>
          </FadeInUp>
        </Container>
      </Box>

      <Box component="section" aria-labelledby="value-title" sx={sectionSx}>
        <Container maxWidth="lg">
          <FadeInUp>
            <Typography
              id="value-title"
              component="h2"
              variant="h3"
              align="center"
              sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
            >
              学習を、一本の流れに
            </Typography>
          </FadeInUp>
          <StaggerGroup
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
              gap: 16,
              marginTop: 32,
            }}
          >
            {valueCards.map(({ icon: Icon, title, description }) => (
              <StaggerItem key={title} style={{ height: '100%' }}>
                <Box
                  sx={{
                    height: '100%',
                    p: { xs: 3, md: 3.5 },
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.borderNeutral}`,
                    borderRadius: `${radius.lg}px`,
                    boxShadow: shadows.card,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      display: 'grid',
                      placeItems: 'center',
                      mb: 2.5,
                      color: colors.gold,
                      bgcolor: colors.goldSoft,
                      borderRadius: `${radius.md}px`,
                    }}
                  >
                    <Icon aria-hidden sx={{ fontSize: 28 }} />
                  </Box>
                  <Typography component="h3" variant="h5" sx={{ mb: 1.25 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ color: colors.textSecondary, lineHeight: 1.8 }}>
                    {description}
                  </Typography>
                </Box>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </Container>
      </Box>

      <Box component="section" aria-labelledby="plans-title" sx={{ ...sectionSx, bgcolor: colors.surface }}>
        <Container maxWidth="md">
          <FadeInUp>
            <Typography
              id="plans-title"
              component="h2"
              variant="h3"
              align="center"
              sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
            >
              無料から、必要な分だけ
            </Typography>
          </FadeInUp>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              mt: 4,
              border: `1px solid ${colors.borderNeutral}`,
              borderRadius: `${radius.lg}px`,
              overflow: 'hidden',
              bgcolor: colors.bg,
              boxShadow: shadows.card,
            }}
          >
            {plans.map((plan, index) => (
              <Box
                key={plan.name}
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderTop: index === 1 ? { xs: `1px solid ${colors.border}`, md: 0 } : 0,
                  borderLeft: index === 1 ? { xs: 0, md: `1px solid ${colors.border}` } : 0,
                }}
              >
                <Typography
                  component="h3"
                  variant="h4"
                  sx={{ color: index === 1 ? colors.goldBright : colors.textPrimary }}
                >
                  {plan.name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, mb: 2.5, color: colors.textSecondary }}>
                  {plan.lead}
                </Typography>
                <Stack spacing={1.5}>
                  {plan.features.map((feature) => (
                    <Stack key={feature} direction="row" spacing={1.25} alignItems="center">
                      <CheckRounded aria-hidden sx={{ flexShrink: 0, fontSize: 20, color: colors.gold }} />
                      <Typography>{feature}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 3.5, pt: 3, textAlign: 'center', borderTop: `1px solid ${colors.border}` }}>
            <Typography component="p" sx={{ color: colors.textSecondary }}>
              プレミアム
            </Typography>
            <Typography
              component="p"
              variant="h3"
              sx={{ mt: 0.5, color: colors.goldBright, fontSize: { xs: '2rem', md: '2.4rem' } }}
            >
              <Box component="span" sx={{ mr: 1, fontSize: '0.55em', color: colors.textSecondary }}>
                月額
              </Box>
              ¥500
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              いつでも解約できます
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box component="section" aria-labelledby="community-title" sx={sectionSx}>
        <Container maxWidth="md">
          <FadeInUp>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr auto' },
                gap: { xs: 2, sm: 3 },
                alignItems: 'center',
                p: { xs: 3, sm: 4 },
                bgcolor: colors.surface,
                border: `1px solid ${colors.borderNeutral}`,
                borderRadius: `${radius.lg}px`,
              }}
            >
              <ForumRounded aria-hidden sx={{ fontSize: 42, color: colors.gold }} />
              <Box>
                <Typography id="community-title" component="h2" variant="h4">
                  同じ目標の人だけが集まる場所
                </Typography>
                <Typography sx={{ mt: 1, color: colors.textSecondary, lineHeight: 1.8 }}>
                  一般的なSNSでは勉強と無関係な情報も混ざります。ここは、診断士合格という一つの目標を持つ人だけのコミュニティです。
                </Typography>
              </Box>
              <Pressable>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/chusho-app/community')}
                  sx={{ minWidth: { sm: 150 }, minHeight: 46 }}
                >
                  コミュニティを見る
                </Button>
              </Pressable>
            </Box>
          </FadeInUp>
        </Container>
      </Box>

      <Box component="section" aria-labelledby="operator-title" sx={{ ...sectionSx, bgcolor: colors.surface }}>
        <Container maxWidth="md">
          <FadeInUp>
            <Typography
              id="operator-title"
              component="h2"
              variant="h3"
              align="center"
              sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' } }}
            >
              運営者から
            </Typography>
            <Typography sx={{ mt: 2.5, mx: 'auto', maxWidth: 720, color: colors.textSecondary, lineHeight: 2 }}>
              独学で中小企業診断士試験に挑んだ経験から、迷わず学習を続けられる場所を目指して作りました。運営元はTechAthletesです。受験生の声を受け取りながら、学びやすさを少しずつ磨いています。
            </Typography>
          </FadeInUp>
        </Container>
      </Box>

      <Box
        component="section"
        aria-labelledby="final-cta-title"
        sx={{
          py: { xs: 8, md: 10 },
          textAlign: 'center',
          borderTop: `1px solid ${colors.border}`,
          background: `linear-gradient(180deg, ${colors.bg}, ${alpha(colors.gold, 0.06)})`,
        }}
      >
        <Container maxWidth="sm">
          <FadeInUp>
            <Typography
              id="final-cta-title"
              component="h2"
              variant="h3"
              sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}
            >
              今日の学習を、ここから。
            </Typography>
            <Pressable style={{ maxWidth: 320, margin: '32px auto 0' }}>
              <Button fullWidth size="large" variant="contained" onClick={startForFree} sx={{ minHeight: 54 }}>
                無料で始める
              </Button>
            </Pressable>
          </FadeInUp>
        </Container>
      </Box>

      <Box component="footer" sx={{ borderTop: `1px solid ${colors.borderNeutral}`, py: 3 }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              © TechAthletes
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/support')}
              sx={{ color: theme.palette.text.secondary }}
            >
              サポート
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default NewLandingPage;
